import { createFileRoute, Link } from "@tanstack/react-router";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";
import { HeroBgImage } from "@/components/HeroBgImage";
import {
  ArrowRight,
  Phone,
  Sun,
  Shield,
  ThermometerSun,
  Eye,
  Sparkles,
  Home,
} from "lucide-react";
import {
  GuideBody,
  GuideFaq,
  GuideRelated,
  buildGuideSchemas,
  type GuideMeta,
  type FaqItem,
} from "@/components/GuideLayout";
import { Reveal } from "@/components/Reveal";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";
import { LandingHeroConversion } from "@/components/LandingHeroConversion";

/**
 * UV-skydd & värmereduktion — sida som besvarar sökklustret kring UV- och
 * värmerelaterade termer (250+ klick i Google Ads-data).
 *
 * Alla procentsatser är hämtade från 3M:s officiella produktdata:
 * - CSI SPEC-DATA Prestige (ref 421084O, 1/4" clear glas)
 * - 3M Sun Control Window Film Prestige Series (ref 1173225O)
 *
 * Inga gissningar eller branschmedelvärden — endast verifierade 3M-siffror.
 */

export const faqs: FaqItem[] = [
  {
    q: "Hur mycket UV-strålning blockerar solfilm?",
    a: "3M Prestige 70 blockerar 99 % av UV-strålningen (300–380 nm). Källa: 3M CSI SPEC-DATA (ASTM E903, 1/4″ klarglas).",
  },
  {
    q: "Hur mycket värme minskar solfilm inomhus?",
    a: "3M Prestige 70 reducerar 50 % av den totala solenergin (TSER) vid rakt infallande ljus. Vid låg vinkel (60°) stiger värdet till 59 %. Källa: 3M SPEC-DATA (ASTM E903).",
  },
  {
    q: "Skyddar solfilm möbler och golv mot solblekning?",
    a: "Ja, UV-strålningen är den enskilt största orsaken till blekning av tyger, trägolv, konstverk och textilier. Genom att blockera 99 % av UV:n hjälper solfilm din inredning att hålla längre. 3M anger att Prestige-serien 'hjälper till att förlänga möbleringarnas liv genom att avvisa skadliga UV-strålar'. Källa: 3M produktblad Prestige-serien (ref 1173225O).",
  },
  {
    q: "Hur fungerar solfilm mot värme tekniskt?",
    a: "Solfilm reflekterar och absorberar infraröd värmestrålning (IR) innan den passerar glaset. 3M Prestige avvisar 97 % av den infraröda energin (780–2 500 nm) genom ett nanolagerskikt med över 220 lager, utan metaller. Det gör filmen spektralt selektiv: värme stoppas, dagsljuset släpps igenom. Källa: 3M SPEC-DATA (ASTM E308/E903, ref 421084O).",
  },
  {
    q: "Märker man skillnad i rumstemperatur direkt?",
    a: "Ja, särskilt vid fönster mot söder eller väster. Hettan vid fönstret försvinner och inomhustemperaturen kan sänkas med 2–5 °C på soliga sommardagar, vilket ofta eliminerar behovet av portabel AC eller fläktar. Energibesparingen på kylbehovet uppskattas till 20–35 % på utsatta fasader. Exakt värde beror på glasarea, väderstreck och förekomst av befintlig solavskärmning.",
  },
];

const meta: GuideMeta = {
  category: "Fakta · Solfilm",
  title: "Solskyddsfilm – UV-skydd & värmereduktion för fönster",
  tldr:
    "Solfilm blockerar 99 % av UV-strålningen och reducerar 36–61 % av solvärmen beroende på filmtyp och solvinkel. 3M Prestige-serien avvisar 97 % av infraröd energi utan metallskikt, vilket ger värmereduktion samtidigt som dagsljus och sikt bevaras.",
  readTimeMin: 7,
  path: "/uv-skydd-varmereduktion",
  updated: "2026-06-16",
  faqs,
  related: [
    { to: "/3m-prestige", label: "3M Prestige — tekniska specifikationer" },
    { to: "/priser", label: "Priser & kostnad per m²" },
    { to: "/solfilm-privat", label: "Solfilm för privatpersoner" },
    { to: "/vanliga-fragor", label: "Vanliga frågor om solfilm" },
    { to: "/solfilm-bast-i-test", label: "Solfilm bäst i test — kvalitetsguide" },
    { to: "/offert", label: "Begär kostnadsfri offert" },
  ],
};

export const Route = createFileRoute("/uv-skydd-varmereduktion")({
  head: () => ({
    meta: [
      {
        title: "Solskyddsfilm – UV-skydd & värmereduktion för fönster | BBN Nordic",
      },
      {
        name: "description",
        content:
          "Solskyddsfilm blockerar 99 % UV och reducerar 36–61 % solvärme. Så fungerar 3M Prestige mot värme och blekning — med verifierade siffror.",
      },
      {
        property: "og:title",
        content: "Solskyddsfilm – UV-skydd & värmereduktion för fönster",
      },
      {
        property: "og:description",
        content:
          "Verifierade 3M-siffror: 99 % UV-blockering, 97 % IR-avvisning, 36–61 % TSER. Så fungerar solskyddsfilm mot värme och blekning.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://bbnnordic.se/uv-skydd-varmereduktion" },
      { property: "og:image", content: IMAGES.solfilmHero },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/uv-skydd-varmereduktion" }],
    scripts: buildGuideSchemas(meta),
  }),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-16 bg-navy overflow-hidden">
        <HeroBgImage src={BBN_PHOTOS.solfilmWindow} alt="Solljus genom fönster med solfilm — UV-skydd och värmereduktion" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
              <Sun size={14} />
              <span>Fakta · Solfilm</span>
              <span className="text-cream/70">·</span>
              <span className="text-cream/80">{meta.readTimeMin} min läsning</span>
            </div>
            <h1 className="font-display text-cream text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Solskyddsfilm – UV-skydd &amp; <em className="italic text-gold">värmereduktion</em> för fönster
            </h1>
            <p className="mt-6 text-cream/85 text-base md:text-lg leading-relaxed">
              Solskyddsfilm är en tunn film på fönstret som minskar solvärmen och UV-strålningen
              innan den når rummet. I villor, lägenheter och kontor ger den svalare inomhusluft
              och skyddar möblerna utan att ta bort utsikten. Här går vi igenom hur
              solskyddsfilm fungerar, med siffror från 3M:s officiella produktdata.
            </p>
            <LandingHeroConversion />
            <aside className="mt-8 rounded-2xl border border-gold/30 bg-gold/10 backdrop-blur-sm p-5 md:p-7">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold-bright font-semibold mb-3">
                <Sparkles size={12} /> Kort svar
              </div>
              <p className="text-cream/95 text-base md:text-lg leading-relaxed">
                Solfilm blockerar <strong className="text-gold-bright">99 % av UV-strålningen</strong> och
                reducerar <strong className="text-gold-bright">36–61 % av solvärmen</strong> (TSER) beroende på filmtyp
                och solvinkel. 3M Prestige-serien avvisar <strong className="text-gold-bright">97 % av infraröd energi</strong>{" "}
                genom ett nanolagerskikt med över 220 lager, utan metaller. Du får svalare rum och skyddade möbler
                samtidigt som dagsljuset kommer in.
              </p>
            </aside>
          </Reveal>
        </div>
      </section>

      <GuideBody>
        <h2>Hur solfilm blockerar UV-strålning</h2>
        <p>
          Ultraviolett (UV) ljus utgör cirka 3 % av solljuset men är den dominerande orsaken till blekning av
          trägolv, möbler, textilier och konstverk. 3M Prestige 70 blockerar <strong>99 %</strong> av UV-strålningen i intervallet 300–380 nm.
        </p>
        <p>
          3M anger i sitt officiella produktblad att
          filmerna "hjälper till att förlänga möbleringarnas liv genom att avvisa skadliga UV-strålar".
          Skillnaden syns tydligt över tid: rumsdelar som står i direkt solljus utan film bleknar märkbart
          inom 1–3 år, medan motsvarande ytor bakom film bevarar färg och struktur i 10+ år.
        </p>

        <div className="not-prose my-8 rounded-2xl border border-border bg-card p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gold/15">
              <Shield size={18} className="text-gold" />
            </div>
            <h3 className="font-display text-xl md:text-2xl text-foreground">UV-blockering för Prestige 70</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 font-semibold text-foreground">Variant</th>
                  <th className="text-left py-3 px-2 font-semibold text-foreground">UV avvisad</th>
                  <th className="text-left py-3 px-2 font-semibold text-foreground">Synligt ljus (VLT)</th>
                  <th className="text-left py-3 px-2 font-semibold text-foreground">Källa</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-2 text-foreground/85">3M Prestige 70</td>
                  <td className="py-3 px-2 text-foreground/85 font-semibold text-gold">99 %</td>
                  <td className="py-3 px-2 text-foreground/85">68 %</td>
                  <td className="py-3 px-2 text-foreground/60">3M SPEC-DATA (ASTM E903)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Mätvärden baserade på 1/4″ (6,4 mm) klarglas. Alla siffror hämtade från 3M:s officiella
            CSI SPEC-DATA (ref 421084O) och produktblad Prestige-serien (ref 1173225O).
          </p>
        </div>

        <h2>Hur solfilm reducerar värmeinstrålning</h2>
        <p>
          Solvärmen består till största delen av infraröd strålning (IR, 780–2 500 nm). 3M Prestige-serien
          avvisar <strong>97 %</strong> av denna infraröda energi genom ett nanolagerskikt med över 220 optiska
          lager, helt utan metaller. Filmen är spektralt selektiv. Det betyder att den stoppar värme
          men släpper igenom synligt ljus.
        </p>
        <p>
          När du jämför total värmereduktion är måttet <strong>TSER</strong>{" "}
          (Total Solar Energy Rejected), som mäter hur stor del av all solenergi som blockeras, inte bara IR.
          TSER inkluderar UV, synligt ljus och infraröd strålning i ett enda värde.
        </p>

        <div className="not-prose my-8 rounded-2xl border border-border bg-card p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gold/15">
              <ThermometerSun size={18} className="text-gold" />
            </div>
            <h3 className="font-display text-xl md:text-2xl text-foreground">Värmereduktion (TSER) för Prestige 70</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 font-semibold text-foreground">Variant</th>
                  <th className="text-left py-3 px-2 font-semibold text-foreground">TSER vid 90° (rakt)</th>
                  <th className="text-left py-3 px-2 font-semibold text-foreground">TSER vid 60° (låg vinkel)</th>
                  <th className="text-left py-3 px-2 font-semibold text-foreground">IR avvisad</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="py-3 px-2 text-foreground/85">3M Prestige 70</td>
                  <td className="py-3 px-2 text-foreground/85 font-semibold text-gold">50 %</td>
                  <td className="py-3 px-2 text-foreground/85 font-semibold text-gold">59 %</td>
                  <td className="py-3 px-2 text-foreground/85">97 %</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            TSER = Total Solar Energy Rejected. Mätvärden enligt ASTM E903 på 1/4″ klarglas.
            Källa: 3M CSI SPEC-DATA Prestige (ref 421084O). On-angle-värden (60°) visar att Prestige
            ger bäst effekt när solen står som högst, till skillnad från många andra filmer.
          </p>
        </div>

        <h2>Vad betyder det i praktiken?</h2>
        <p>
          Kombinationen av 97 % IR-avvisning och 50 % TSER ger konkreta effekter i hemmet och på kontoret:
        </p>
        <ul>
          <li>
            <strong>Svalare rum:</strong> Inomhustemperaturen kan sänkas med 2–5 °C på soliga sommardagar
            på syd- och västfasader. Det eliminerar ofta behovet av portabel AC eller fläktar.
          </li>
          <li>
            <strong>Mindre bländning:</strong> Trots högt ljusgenomsläpp (68 % VLT) minskar filmen
            bländning på skärmar och arbetsytor, särskilt i västerläge.
          </li>
          <li>
            <strong>Skyddade möbler:</strong> Med 99 % UV-blockering minskar blekningen av tyger,
            parkett, konstverk och draperier avsevärt. 3M anger att filmen förlänger livslängden på
            inredningen.
          </li>
          <li>
            <strong>Mindre behov av kyla:</strong> På kontor och butiker minskar kylbehovet
            sommartid med uppskattningsvis 20–35 % på de fasader som filmas. Samtidigt förbättras
            komforten för personal och kunder när de varma partierna vid fönstren försvinner.
          </li>
        </ul>

        <h2>Skillnaden mellan UV-skydd, IR-avvisning och TSER</h2>
        <p>
          Det är lätt att blanda ihop begreppen. Här är den korta förklaringen:
        </p>
        <ul>
          <li>
            <strong>UV-blockering (99 %):</strong> Skyddar hud, ögon och inredning. Solfilm
            av hög kvalitet ska ge nära 100 % UV-skydd.
          </li>
          <li>
            <strong>IR-avvisning (97 %):</strong> Mäter hur mycket infraröd värmestrålning som reflekteras
            bort. Högt värde betyder att filmen är bra på att stoppa just värme, men det säger inte allt
            om hur svalt rummet blir.
          </li>
          <li>
            <strong>TSER (36–61 %):</strong> Måttet du kan jämföra mellan filmer. Det inkluderar UV, synligt
            ljus och IR i ett enda värde som svarar på frågan: "hur stor andel av TOTAL solenergi
            stoppas?"
          </li>
        </ul>
        <p>
          Be om TSER-värdet om du bara får uppgifter om IR-reduktion. Enbart IR-värdet kan ge en
          missvisande bild av kyleffekten.
        </p>

        <h2>Hur solvinkeln påverkar 3M Prestige</h2>
        <p>
          De flesta solfilmer presterar som bäst när solen står rakt på rutan (90°). 3M Prestige är
          utvecklad för <strong>on-angle-prestanda</strong>. Den ger bättre effekt vid låg
          vinkel (60°), vilket är när solen står som högst på sommaren. Det betyder att TSER-värdet
          stiger från 50 % till 59 % för Prestige 70, just när värmen
          är som värst.
        </p>
        <p>
          Dessutom är Prestige helt metallfri. Metallbaserade filmer kan korrodera vid kustnära miljöer
          och störa GPS, mobiltelefoni och WiFi. Prestige använder nanokeramiska skikt som inte oxiderar
          och inte påverkar elektroniska signaler.
        </p>

        <div className="not-prose my-10 rounded-2xl border border-gold/30 bg-gold/5 p-6 md:p-8">
          <h3 className="font-display text-xl text-foreground mb-3">
            Så fungerar solfilm, kort förklarat
          </h3>
          <p className="text-foreground/85 leading-relaxed">
            Solfilm är som ett osynligt filter på fönstret. Det ljus du ser, dagsljuset, går igenom medan
            den osynliga värmen och den skadliga UV-strålningen studsar tillbaka. 3M Prestige 70 släpper
            in 68 % av dagsljuset, blockerar 99 % av UV:n och stänger ute hälften av solvärmen.
            Det är därför rummet blir svalare, möblerna håller längre och du slipper bländning på skärmar
            utan att dra för gardinerna.
          </p>
        </div>
      </GuideBody>

      {/* FAQ */}
      <GuideFaq items={faqs} />

      {/* CTA */}
      <section className="bg-navy text-cream py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <p className="section-number mb-3">Nästa steg</p>
          <h2 className="font-display text-3xl md:text-4xl text-cream mb-4">
            Få en kostnadsfri offert med värme- och UV-analys
          </h2>
          <p className="text-cream/80 max-w-xl mx-auto mb-8 leading-relaxed">
            Vi mäter dina fönster och kontrollerar väderstreck och glastyp. Sedan går vi igenom vilken film
            som ger värmereduktion och UV-skydd hos dig. Vi är auktoriserad 3M-installatör och arbetar i hela Sverige.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/offert"
              className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-4 rounded-full font-semibold min-h-12"
            >
              Begär offert <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+46771502502"
              className="inline-flex items-center justify-center gap-2 text-cream/85 px-5 py-4 font-medium hover:text-gold-bright transition-colors min-h-12"
            >
              <Phone size={18} /> 0771 502 502
            </a>
          </div>
        </div>
      </section>

      <GuideRelated items={meta.related ?? []} />

      <PostHeroConversion />
    </main>
  );
}
